import { DateTime as DT } from 'luxon';
import { useTranslation } from 'react-i18next';
import type { DocumentDirectoryType, ObjectProposalGenerationType } from '@econtractor/types';
import { GenerationLogCommentCell } from 'modules/templates';
import { UserNameItem } from 'modules/user';
import { format$Price, formatPrice } from 'utils/helpers/functions/formatPrice';
import { withLocalCurrency } from 'utils/helpers/currency/currencyHelpers';
import { safeFloatParse } from 'utils/helpers/safeParsing';
import ShareDocument from 'components/ShareLink';
import DocumentActions from './DocumentActions';

const DOCUMENT_TYPE_BADGE_CLASS: Record<string, string> = {
    contract: 'bg-light-primary text-primary',
    invoice: 'bg-light-info text-info',
    act: 'bg-light-success text-success',
    other: 'bg-light-secondary text-secondary',
    proposal: 'bg-light-warning text-warning',
};

const formatGenerationAmount = (amount: number, isUsd: boolean) => {
    const value = safeFloatParse(amount || 0);
    if (isUsd) return format$Price(value);
    return value >= 0
        ? withLocalCurrency(formatPrice(value))
        : withLocalCurrency(formatPrice(-value), true);
};

export interface DocumentTableRowProps {
    row: ObjectProposalGenerationType;
    deletingId: number | null;
    onCreateFromGeneration: (row: ObjectProposalGenerationType) => void;
    onToWarehouse: (row: ObjectProposalGenerationType) => void;
    onPayment: (row: ObjectProposalGenerationType) => void;
    onDelete: (row: ObjectProposalGenerationType) => void;
    onSendEmail: (row: ObjectProposalGenerationType) => void;
}

const DocumentTableRow = ({
    row,
    deletingId,
    onCreateFromGeneration,
    onToWarehouse,
    onPayment,
    onDelete,
    onSendEmail,
}: DocumentTableRowProps) => {
    const { t } = useTranslation(['Objects', 'Document', 'Proposal']);
    const generatedAt = DT.fromMillis(row.generatedCreated || 0);
    const isDeleted = Boolean(Number(row.deleted || 0));
    const isUsd = Boolean(row.proposalUsd);
    const typeKey = (row.proposalType || 'other') as DocumentDirectoryType | 'proposal';
    const badgeClass = DOCUMENT_TYPE_BADGE_CLASS[typeKey] || DOCUMENT_TYPE_BADGE_CLASS.other;
    const paidAmount = safeFloatParse(row.proposalPay || 0);
    const fileUrl = String(row.filePublicUrl || '').trim();

    return (
        <tr className={isDeleted ? 'is-deleted' : undefined}>
            <td className="user-cell">
                <UserNameItem
                    user={{
                        ID: row.ownerId,
                        userName: row.userName || `#${row.ownerId}`,
                        userEmail: row.userEmail || '',
                        userPicture: row.userPicture || '',
                    }}
                />
            </td>
            <td className="date-cell">
                <div>{generatedAt.toFormat('dd.MM.yyyy')}</div>
                <div className="text-muted">{generatedAt.toFormat('HH:mm')}</div>
            </td>
            <td className="type-cell">
                <span className={`badge ${badgeClass}`}>
                    {t(`Document:documentTypes.${typeKey}`, {
                        defaultValue: typeKey,
                    })}
                </span>
                <div className="text-muted fs-12 text-truncate mt-1" title={row.proposalName}>
                    {row.proposalName || '-'}
                </div>
            </td>
            <td className="amount-cell text-end">
                <div>{formatGenerationAmount(row.proposalAmount, isUsd)}</div>
                {paidAmount !== 0 ? (
                    <div className="text-success fs-12">
                        {formatGenerationAmount(paidAmount, isUsd)}
                    </div>
                ) : (
                    <div className="text-muted fs-12">-</div>
                )}
            </td>
            <td className="comment-cell">
                <GenerationLogCommentCell
                    generationComment={row.generationComment}
                    proposalEmailBody={row.proposalEmailBody}
                    generationTitle={t('Proposal:edit.generate.logCommentHint')}
                    emailBodyTitle={t('Proposal:edit.generate.logEmailBodyHint')}
                />
            </td>
            <td className="file-cell">
                {fileUrl ? (
                    <div className="file-cell-row">
                        <a href={fileUrl} target="_blank" rel="noreferrer">
                            {row.fileName || t('Document:edit.generate.logOpenFile')}
                        </a>
                        <ShareDocument
                            text={row.proposalName || ''}
                            link={fileUrl}
                            inputMessageDlg
                            disabled={isDeleted}
                        />
                    </div>
                ) : (
                    '-'
                )}
            </td>
            <td className="actions">
                <DocumentActions
                    row={row}
                    deletingId={deletingId}
                    onCreateFromGeneration={onCreateFromGeneration}
                    onToWarehouse={onToWarehouse}
                    onPayment={onPayment}
                    onDelete={onDelete}
                    onSendEmail={onSendEmail}
                />
            </td>
        </tr>
    );
};

export default DocumentTableRow;
